import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase, db } from '@/lib/supabase';
import { readStoreInviteToken } from '@/lib/storeInviteContext';

function isBuyer(user: { user_metadata?: Record<string, unknown> } | null): boolean {
  return user?.user_metadata?.role === 'buyer';
}

function buyerStoreSlug(user: { user_metadata?: Record<string, unknown> } | null): string {
  const raw = user?.user_metadata?.buyer_store_slug;
  return typeof raw === 'string' ? raw.trim() : '';
}

const SLUG_SAFE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

/**
 * Post-email-verification routing on main app entry points only.
 * Buyers never go to seller onboarding; sellers go to onboarding or dashboard.
 */
export function VerificationRedirectHandler() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const handledRef = useRef(false);

  useEffect(() => {
    if (loading || !user) return;

    if (handledRef.current) return;

    // Email/password users must confirm; OAuth (e.g. Google) is treated as verified by Supabase but
    // email_confirmed_at can be briefly unset during hash exchange — allow known OAuth providers.
    if (!user.email_confirmed_at) {
      const ids = (user as { identities?: { provider?: string }[] }).identities;
      const oauth = ids?.some((i) => i.provider && i.provider !== 'email');
      if (!oauth) return;
    }

    const path = location.pathname;

    const isEntryPoint =
      path === '/' || path === '/auth/callback' || path.startsWith('/verify-email');
    if (!isEntryPoint) return;

    if (path.startsWith('/onboarding') || path.startsWith('/dashboard')) return;

    const checkAndRedirect = async () => {
      if (isBuyer(user)) {
        const slug = buyerStoreSlug(user);
        if (slug && SLUG_SAFE.test(slug)) {
          navigate(`/store/${encodeURIComponent(slug)}/account`, { replace: true });
        } else {
          if (import.meta.env.DEV) {
            console.warn(
              '[VerificationRedirectHandler] buyer without valid buyer_store_slug — sending to /'
            );
          }
          navigate('/', { replace: true });
        }
        handledRef.current = true;
        return;
      }

      try {
        const pendingInvite = readStoreInviteToken();
        if (pendingInvite) {
          navigate(`/accept-invite?token=${encodeURIComponent(pendingInvite)}`, { replace: true });
          handledRef.current = true;
          return;
        }

        const { count: ownedCount, error: ownedErr } = await db
          .from('stores')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id);
        const { count: memberCount, error: memberErr } = await db
          .from('store_members')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id);

        if (ownedErr || memberErr) {
          if (import.meta.env.DEV) {
            console.warn('[VerificationRedirectHandler] store access check failed', ownedErr ?? memberErr);
          }
          navigate('/onboarding', { replace: true });
          handledRef.current = true;
          return;
        }

        const hasStore =
          (ownedCount ?? 0) > 0 || (memberCount ?? 0) > 0;
        navigate(hasStore ? '/dashboard' : '/onboarding', { replace: true });
        handledRef.current = true;
      } catch {
        navigate('/onboarding', { replace: true });
        handledRef.current = true;
      }
    };

    void checkAndRedirect();
  }, [user, loading, navigate, location.pathname]);

  return null;
}
